import { Close } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import React, { MouseEventHandler } from 'react';

import { getDailySpendV2 } from '../../api/ventura/lineItem';
import { useConfig } from '../../hooks/useConfig';
import { addCommas, formatServerDate, mmddyy, parseDollarsFromApiPennyValue, round } from '../../util/helpers';
import ContractReportsPageLink from '../Contracts/ContractReportsPageLink';
import LineItemSpendStatus from './LineItemSpendStatus';

interface LineItemSpendModalProps {
  lineItemId: number;
  contractId: number;
  onClose: MouseEventHandler | undefined;
}

const headCells: GridColDef[] = [
  {
    field: 'serverDate',
    type: 'string',
    headerName: 'Date',
    renderCell: (params) => mmddyy(formatServerDate(params.value)),
    display: 'flex',
  },
  {
    field: 'budget',
    type: 'number',
    headerName: 'Impression Goal',
    renderCell: (params) => (params.value === undefined ? '--' : addCommas(params.value)),
    display: 'flex',
  },
  {
    field: 'impressions',
    type: 'number',
    headerName: 'Impressions',
    renderCell: (params) => addCommas(params.value),
    display: 'flex',
  },
  {
    field: 'clicks',
    type: 'number',
    renderCell: (params) => addCommas(params.value),
    headerName: 'Clicks',
    display: 'flex',
  },
  {
    field: 'ctr',
    type: 'number',
    headerName: 'CTR',
    renderCell: (params) => `${params.value === undefined ? '--' : round(params.value)}%`,
    display: 'flex',
  },
  {
    field: 'cents_cpm',
    type: 'number',
    headerName: 'Spend',
    renderCell: (params) => `$${parseDollarsFromApiPennyValue(params.value)}`,
    display: 'flex',
  },
];

function LineItemSpendModal({ lineItemId, contractId, onClose }: LineItemSpendModalProps) {
  const { apiHost } = useConfig();

  const { data: dailySpendData, isLoading: isDailySpendDataLoading } = useQuery({
    queryKey: ['lineItemSpendV2', lineItemId],
    queryFn: () => getDailySpendV2(apiHost, lineItemId),
  });

  // V2 (BigQuery daily_pacing_output) rows: { report_day: 'YYYY-MM-DD', target_spend,
  // impressions, clicks, ... }. target_spend feeds the Impression Goal column; Spend is
  // left at $0 for now.
  const data = dailySpendData
    ?.map((dailySpendDataItem, index) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { report_day, clicks, impressions } = dailySpendDataItem;
      const serverDate = formatServerDate(report_day);
      return {
        // Datagrid requires an ID field
        id: index,
        ...dailySpendDataItem,
        serverDate,
        budget: dailySpendDataItem.target_spend,
        cents_cpm: 0,
        ctr: (impressions && (clicks / impressions) * 100) || undefined,
      };
    })
    .sort((a, b) => (a.serverDate < b.serverDate ? 1 : -1));

  return (
    <Stack sx={{ minHeight: '200px', p: 2 }}>
      <Stack direction="row" justifyContent="space-between" justifyItems="center">
        <Typography variant="h6" sx={{ mt: '8px' }}>
          Daily Spend Activity
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <ContractReportsPageLink
            contractId={contractId}
            selectedLineItemId={lineItemId}
            label="View Full Performance"
            onClick={onClose as React.MouseEventHandler}
          />
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </Stack>
      <LineItemSpendStatus lineItemId={lineItemId} />
      <Stack mt={2}>
        <DataGrid columns={headCells} rows={data || []} loading={isDailySpendDataLoading} />
      </Stack>
    </Stack>
  );
}

export default LineItemSpendModal;
